import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { useLazyQuery, useMutation } from '@apollo/client';
import { QUERY_VALIDATE_ORDER } from '../../utils/queries';
import { CREATE_CHECKOUT_SESSION } from '../../utils/mutations';
import { idbPromise } from '../../utils/helpers';
import CartItem from '../CartItem';
import Auth from '../../utils/auth';
import { TOGGLE_CART, ADD_MULTIPLE_TO_CART, UPDATE_CART_LINES_INSTOCK } from '../../utils/actions';
import './style.css';

const LOCAL_STORE_USER_ID = 'oshikem_user_id';

const Cart = () => {
  const cartState = useSelector(state => state.cart); // read Redux state
  const cartOpen = useSelector(state => state.cartOpen);
  const dispatch = useDispatch();

  const [checkoutOk, setCheckoutOk] = useState(false);

  const [validateOrder, { data: validateData, loading: validateLoading }] =
    useLazyQuery(QUERY_VALIDATE_ORDER);

  const [createCheckout, { data: checkoutData, loading: checkoutLoading, error: checkoutError }] =
    useMutation(CREATE_CHECKOUT_SESSION);

  const userId = window.localStorage.getItem(LOCAL_STORE_USER_ID);

  function getCartProductIdsAndQuantities(cart) {
    return cart.map(({purchaseQuantity, _id}) =>
      ({ quantity: purchaseQuantity, product_id: _id }));
  }

  const getCartLines = useCallback(
    () => getCartProductIdsAndQuantities(cartState),
    [cartState]);

  useEffect(() => {
      if (cartOpen) {
        if (cartState.length) {
          console.info('cart is open - validating order')
          // Check if stocking info is present; if it is, skip re-validating.
          // otherwise, this loops endlessly because of the cart update to change inStock flags!

          const hasStockedData = cartState.reduce((flag, item) => flag && typeof item.inStock !== 'undefined', true);
          if (! hasStockedData) {
            validateOrder({ variables: { lines: getCartLines(cartState) } })
              .then(({data}) => dispatch({ type: UPDATE_CART_LINES_INSTOCK, products: data.validate }));
          }
        }
      }
    }, [cartOpen, cartState, dispatch, validateOrder, getCartLines]);  // run one time on component load

  useEffect(() => {
      if (validateData) {
        console.debug(validateData)
        setCheckoutOk(
          cartState.reduce(
            (ok, item) => ok && validateData.validate.find(line => line.in_stock && line.active),
            true
          ) );
      }
    }, [validateData, cartState]);

  useEffect(() => {
      if (checkoutData) {
        loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY)
          .then(res => res.redirectToCheckout({ sessionId: checkoutData.checkout.session }));
      } else if (checkoutError) {
        alert('There was a problem submitting checkout - perhaps products are no longer valid')
        console.error(checkoutError)
      }
    }, [checkoutData, checkoutError]);

  useEffect(() => {
      async function getCart() {
        const cart = await idbPromise('cart', 'get');
        dispatch({ type: ADD_MULTIPLE_TO_CART, products: [...cart] });
      }

      if (!cartState.length) {
        getCart();
      }
    }, [cartState.length, dispatch]);

  function toggleCart() {
    dispatch({ type: TOGGLE_CART });
  }

  function calculateTotal() {
    let sum = 0;
    cartState.forEach((item) => {
      sum += item.price_usd * item.purchaseQuantity;
    });
    return sum.toFixed(2);
  }

  function submitCheckout() {
    return createCheckout({
          variables: { userId, lines: getCartProductIdsAndQuantities(cartState) }
        })
      .then(() => console.debug('submitCheckout: QUERY DONE'));
  }

  if (!cartOpen) {
    return (
      <div className="cart-closed" onClick={toggleCart}>
        <i className="bi bi-cart"></i>
      </div>
    );
  }

  function checkCartInStock() {
    return validateLoading || ! cartState.length || ! cartState.find(p => ! p.inStock);
  }

  return (
    <div className="cart">
      <div className="close" onClick={toggleCart}>
        <i className="bi bi-x-lg"></i>
      </div>
      <h2>Shopping Cart</h2>
      {cartState.length ? (
        <div>
          {cartState.map((item) => (
            <CartItem key={item._id} item={item} />
          ))}

          {
            checkCartInStock() ? null :
              (<div className="alert alert-warning">
                Some products are out of stock. Please remove before checking out.
              </div>)
          }

          <div className="flex-row space-between">
            <strong>Total: ${calculateTotal()}</strong>

            {true || Auth.loggedIn() ? (
              <button disabled={checkoutOk && ! checkoutLoading ? null : 'disabled'} onClick={submitCheckout}>Checkout</button>
            ) : (
              <span>(log in to check out)</span>
            )}
          </div>
        </div>
      ) : (
        <div>Cart is empty</div>
      )}
    </div>
  );
};

export default Cart;
